// src/lib/posts/labels.ts
//
// Single source of truth for turning post taxonomy data into human strings.
// Raw slugs ("casting-female-talent-fm", "new-york-city") were leaking into
// breadcrumbs, card cities, location pills, and section headings across the
// posts surfaces (2026-07-10 critique, P0). Every guest-facing render of a
// slug goes through here.

import { sectionForCategory, type PostSection } from './sections';

/** "casting-female-talent-fm" -> "Casting Female Talent Fm" (title case, separators to spaces). */
export function humanizeSlug(s: string | null | undefined): string {
  return String(s ?? '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** City/metro display: "new-york-city" or "NEW YORK" -> "New York City" / "New York". */
export function friendlyCity(s: string | null | undefined): string {
  const raw = String(s ?? '').trim();
  if (!raw) return '';
  return raw
    .toLowerCase()
    .split(/[-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * A bucket IS a post section: same four values, same meaning. The names differ
 * only because these are the badge and filter surfaces and those are the
 * browse and create surfaces.
 */
export type BucketKey = PostSection;

export const BUCKET_LABELS: Record<BucketKey, string> = {
  hiring: 'Now Hiring',
  connect: 'Collaborate',
  looking: 'Available for Work',
  services: 'Services',
};

/**
 * One bucket resolver for card, category page, post detail and the metro feed
 * filter. The detail page previously kept its own pre-2026-06-19-taxonomy
 * fallback regex, so the same post could render COLLABORATE on its card and
 * NOW HIRING on its detail page.
 *
 * The rule itself is not here. It is sectionForCategory in ./sections, shared
 * with the create picker and the metro browse page, so a post cannot carry one
 * bucket on its badge and sit under a different heading two scrolls down. Until
 * 2026-09-01 this function collapsed the `looking` type into `hiring`, which put
 * a NOW HIRING ribbon on every available-* post: a performer advertising herself,
 * badged as a producer offering paid work, on the most public surface the site has.
 *
 * ONE difference from the shared rule, and it is deliberate. sectionForCategory
 * returns null for anything it cannot place, which is right for a picker: a
 * category in no section is better than one in the wrong section. A badge is not
 * a picker. A card with no ribbon is a visible defect, so anything the shared
 * rule declines falls through to the legacy readings below and finally to
 * services. Those legacy branches exist for OLD POST ROWS, not for live
 * categories: every category in the production Categories table carries a type
 * and resolves above (live-verified against /posts/los-angeles, 2026-09-01).
 */
export function bucketForCategory(
  slugOrDesc: string | null | undefined,
  apiType?: string | null
): BucketKey | null {
  const placed = sectionForCategory({
    slug: String(slugOrDesc ?? ''),
    type: apiType ?? undefined,
  });
  if (placed) return placed;

  const s = String(slugOrDesc ?? '').toLowerCase();
  if (!s || s === 'general' || s === 'post') return null;
  // Legacy escort-coded slugs, retired by the 2026-06-19 remap but still on old
  // post rows. `scene` covers the hand-curated male-female-scenes family.
  if (/scene/.test(s)) return 'hiring';
  // Everything else (audio, photo, hmu, studio, ...) is a service.
  return 'services';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "2026-10-13T02:42:49.927" -> { iso: "2026-10-13", label: "Oct 13, 2026" }.
 * The API's timestamps carry no offset, so a Date would read them in the
 * runtime's own zone and the server and the browser could print different
 * days (a hydration mismatch). The calendar date is read from the string.
 */
export function postedDate(input: string | null | undefined): { iso: string; label: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(input ?? '').trim());
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { iso: `${m[1]}-${m[2]}-${m[3]}`, label: `${MONTHS[month - 1]} ${day}, ${m[1]}` };
}
